import React from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { translations } from '@/locale';
import { OrganizationMemberAdd } from '@/domains';

import { InstanceProps } from '@/components/Modal';
import ModalHeader from '@/components/Modal/ModalHeader';
import Typography from '@/components/Typography';

import { inviteOrganizationMember } from '@/services/api/organizations';
import { Role } from '@/constants';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const fields = translations.inputs;
const texts = translations.modals.addMember;

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  role: z.enum([Role.OrgAdmin, Role.Cashier, Role.Member], {
    required_error: 'Role is required'
  }),
  email: z.string().email('Invalid email address')
});

type FormValues = z.infer<typeof formSchema>;

export interface Props extends InstanceProps {
  orgId: string;
  onMemberInvited: () => any;
}

const InviteOrgMemberModal: React.FC<Props & WrappedComponentProps> = ({ close, orgId, onMemberInvited, intl }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      role: Role.Member,
      email: ''
    }
  });

  async function onSubmit(values: FormValues) {
    try {
      await inviteOrganizationMember(orgId, values as OrganizationMemberAdd);
      close();
      onMemberInvited();
    } catch (error) {
      console.error('Failed to invite member:', error);
    }
  }

  return (
    <Card className="w-[450px]">
      <CardHeader>
        <ModalHeader close={close}>
          <Typography is="p" className="font-bold">
            <FormattedMessage id={texts.title} />
          </Typography>
        </ModalHeader>
        <CardDescription>
          The member will receive an email invitation that will allow them to join the organization.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{intl.formatMessage({ id: fields.name.label })}</FormLabel>
                  <FormControl>
                    <Input placeholder={intl.formatMessage({ id: fields.name.placeholder })} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{intl.formatMessage({ id: fields.role.label })}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={intl.formatMessage({ id: fields.role.placeholder })} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {[Role.OrgAdmin, Role.Cashier, Role.Member].map((role) => (
                        <SelectItem key={role} value={role}>
                          <FormattedMessage id={translations.roles[role]} />
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{intl.formatMessage({ id: fields.email.label })}</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder={intl.formatMessage({ id: fields.email.placeholder })} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              <FormattedMessage id={texts.confirm} />
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default injectIntl(InviteOrgMemberModal);
